import axios from 'axios';
import { getGlobalStore } from '../../views/pages/AppStore';
import { attachLoading } from '../js/loading';

const apiClient = axios.create({
	baseURL: '/',
	headers: {
		'Content-Type': 'application/json; charset=UTF-8',
	},
	withCredentials: true // 세션 쿠키를 위해 추가
});

// 전역 로딩 인터셉터 ( 세션 만료 처리보다 먼저 실행되어야 하므로 최상단에 등록 )
attachLoading(apiClient);

const excludeCtxUrls = [
	'/sys/cntxtPth/findLatestCntxtPth.json' // Context Path 정보 호출
	, '/sys/loginPolicy/getLoginMode.json' // 로그인 모드 정보 호출
	, '/sys/loginPolicy/findByStorageMode.json' // 스토리지 모드 정보 호출
]

// JWT authorization 헤더값 → payload 객체 디코딩 (실패 시 null)
function decodeJwtPayload(authorization) {
	try {
		const base64String = authorization.split('.')[1];
		const base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));
		return JSON.parse(jsonPayload);
	} catch (e) {
		return null;
	}
}

// 액세스토큰 만료 시 요청 전 선제 재발급
let proactiveRefreshPromise = null;

async function ensureFreshAccessToken(store) {
	const authorization = store?.state.authorization;
	if (!authorization) return;
	const payload = decodeJwtPayload(authorization);
	if (!payload || !payload.exp) return;

	const now = Math.floor(Date.now() / 1000);
	// 만료 3초 전까지는 그대로 사용
	if (payload.exp > now + 3) return;

	// 만료(임박) → 재발급
	if (!proactiveRefreshPromise) {
		proactiveRefreshPromise = axios.post('/refresh/tokenReissue.json', {}, {
			withCredentials: true
		}).then(res => {
			store.commit('setAuthorization', res.headers.authorization);
			const mbr = decodeJwtPayload(store.state.authorization);
			if (mbr) {
				store.commit('setMbrNm', mbr.mbrNm);
				store.commit('setRoles', mbr.roles);
			}
		}).finally(() => {
			proactiveRefreshPromise = null;
		});
	}
	try {
		await proactiveRefreshPromise;
	} catch (e) {
	}
}

apiClient.interceptors.request.use(
	async config => {
		const store = getGlobalStore();
		const excludeCtxUrl = excludeCtxUrls.some(url => config.url.includes(url));
		const contextPath = store?.state.contextPath || '';
		const loginMode = store?.state.loginMode || 'J'; // 기본값은 JWT 모드

		// JWT 모드: 액세스토큰 만료 시 요청 전 선제 재발급
		if (loginMode === 'J' && !excludeCtxUrl && !config.url.includes('/refresh/tokenReissue.json')) {
			await ensureFreshAccessToken(store);
		}

		if (!excludeCtxUrl) {
			config.url = contextPath + config.url;
		}

		// 로그인 모드에 따른 헤더 설정
		if (loginMode === 'J') {
			config.headers.Authorization = store?.state.authorization;
		} else if (loginMode === 'S') {
			delete config.headers.Authorization;
			config.withCredentials = true;
		}
		return config;
	},
	error => {
		return Promise.reject(error);
	}
)

apiClient.interceptors.response.use(
	response => {
		return response;
	},
	async error => {
		if (!error.response) {
			return Promise.reject(error);
		}

		// 403 접근 권한 에러
		if (error.response.status == 403 && error.response.data.message == '접근 권한이 없습니다.') {
			window.location.href = (getGlobalStore()?.state.contextPath || '') + '/'; //메인으로 이동
			return Promise.reject(error);
		}

		const originalReq = error.config;
		const store = getGlobalStore();
		const loginMode = store?.state.loginMode || 'J';

		// 토큰 재발급 API 자체의 에러는 재시도 안 함
		if (originalReq.url.includes('/refresh/tokenReissue.json')) {
			const message = error.response?.data?.message || '로그인이 만료되었습니다.';
			handleSessionExpired(store, message);
			return new Promise(() => {}); // 페이지 이동 후 소멸, 컴포넌트 catch 실행 방지
		}

		// 401 에러 처리
		if (error.response.status === 401) {
			// 탈퇴 처리된 회원이면 재발급 시도 없이 바로 로그아웃
			if (error.response.data?.message === '탈퇴 처리된 계정입니다.') {
				handleSessionExpired(store, error.response.data.message);
				return new Promise(() => {});
			}

			// 일반 토큰 만료 - 재시도
			if (!originalReq._retry) {
				originalReq._retry = true;

				if (loginMode === 'J') {
					// JWT 모드: 토큰 재발급 시도
					try {
						const res = await axios.post('/refresh/tokenReissue.json', {}, {
							withCredentials: true
						});

						store.commit('setAuthorization', res.headers.authorization);
						originalReq.headers.Authorization = store.state.authorization;

						/** jwt토큰 디코딩 **/
						const base64String = store.state.authorization.split('.')[1];
						const base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');
						const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
							return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
						}).join(''));
						const mbr = JSON.parse(jsonPayload);

						store.commit("setMbrNm", mbr.mbrNm);
						store.commit('setRoles', mbr.roles);
						/** jwt토큰 디코딩 끝 **/

						return apiClient(originalReq);
					} catch (refreshError) {
						const message = refreshError.response?.data?.message || '로그인이 만료되었습니다.';
						handleSessionExpired(store, message);
						return new Promise(() => {}); // 페이지 이동 후 소멸, 컴포넌트 catch 실행 방지
					}
				} else if (loginMode === 'S') {
					// 세션 모드: 토큰 재발급 없이 바로 로그인 페이지로
					const message = error.response?.data?.message || '로그인이 만료되었습니다.';
					handleSessionExpired(store, message);
					return new Promise(() => {}); // 페이지 이동 후 소멸, 컴포넌트 catch 실행 방지
				}
			}
		}

		return Promise.reject(error);
	}
)

/**
 * 세션/인증 만료 처리 함수
 * - 중복 실행 방지 플래그로 alert 다중 호출 차단
 */
let isSessionExpiredHandling = false;

function handleSessionExpired(store, message = '로그인이 만료되었습니다.') {
	// 이미 처리 중이면 무시
	if (isSessionExpiredHandling) return;
	isSessionExpiredHandling = true;

	// Store 초기화 전에 경로 먼저 저장
	const admPath = store.state.path?.includes("/adm");
	const contextPath = store.state.contextPath || '';

	const redirect = window.location.pathname + window.location.search;
	sessionStorage.setItem("redirect", redirect);

	// 로그인 정보가 남아 있었는지
	const hadLogin = !!(store.state.authorization || store.state.mbrId);

	// Store 초기화
	store.commit("setStoreReset");

	// localStorage도 정리
	localStorage.removeItem("authorization");
	localStorage.removeItem("vuex");

	// 사용자에게 알림
	alert(`${message}\n다시 로그인해 주세요.`);

	// 만료된 로그인 정보가 있었으면 현재 페이지 재진입
	if (hadLogin) {
		window.location.reload();
		return;
	}

	// 로그인 정보 없이 인증 API 호출 시 로그인 페이지로 강제 이동 (재진입 시 같은 401 반복 방지)
	const loginUrl = admPath ? contextPath + "/cmslogin.page" : contextPath + "/login.page";
	window.location.href = loginUrl;
}

export default apiClient;